import { create } from 'zustand';

import { getCartApi, mutateCartApi } from '@/entities/cart/api/cartApi';
import type { CartOperation } from '@/entities/cart/config/mutations';
import type {
  Cart,
  CartApiResponse,
  CartItem,
  CartPrice,
} from '@/entities/cart/config/types';
import { getAvailableCartItems } from '@/entities/cart/lib/availability';
import type { Product } from '@/entities/product/config/types';

import { createBrowserCartSync } from './browserCartSync';
import type { CartSync } from './cartSync';

interface CartStore extends Cart {
  priceDetails: CartPrice | null;
  needsSync: boolean;
  syncError: string | null;
  addToCart(product: Product, priceId: number): void;
  removeFromCart(productId: number, priceId: number): void;
  adjustQuantity(productId: number, priceId: number, delta: -1 | 1): void;
  updateCartItemPrice(
    productId: number,
    oldPriceId: number,
    newPriceId: number,
  ): void;
  clearCart(): void;
}
function totals(items: CartItem[]) {
  const available = getAvailableCartItems(items);
  return {
    totalCount: available.reduce((sum, item) => sum + item.quantity, 0),
    totalPrice: available.reduce(
      (sum, item) =>
        sum +
        (item.selectedPrice.price_with_personal_discount ??
          item.selectedPrice.price ??
          0) *
          item.quantity,
      0,
    ),
  };
}
function applySnapshot(data: CartApiResponse) {
  const calculated = totals(data.product_list);
  useCartStore.setState({
    items: data.product_list,
    ...calculated,
    totalPrice: data.price?.total_price ?? calculated.totalPrice,
    priceDetails: data.price,
  });
}
let sync: CartSync | undefined;
export function getCartSync(): CartSync {
  if (!sync)
    sync = createBrowserCartSync({
      getCart: getCartApi,
      mutateCart: mutateCartApi,
      applySnapshot,
      onSyncState: ({ pending, error }) =>
        useCartStore.setState({
          needsSync: pending > 0,
          syncError: error?.message ?? null,
        }),
    });
  return sync;
}
function change(items: CartItem[], operation: CartOperation) {
  useCartStore.setState({ items, ...totals(items), needsSync: true });
  void getCartSync()
    .enqueue(operation)
    .catch(() => {
      /* coordinator exposes the error in the store */
    });
}
export const useCartStore = create<CartStore>()((set, get) => ({
  items: [],
  totalPrice: 0,
  totalCount: 0,
  priceDetails: null,
  needsSync: false,
  syncError: null,
  addToCart(product, priceId) {
    const selectedPrice = product.prices.find((price) => price.id === priceId);
    if (!selectedPrice?.is_available || selectedPrice.price === null) return;
    const found = get().items.some((item) => item.selectedPrice.id === priceId);
    const items = found
      ? get().items.map((item) =>
          item.selectedPrice.id === priceId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        )
      : [...get().items, { product, selectedPrice, quantity: 1 }];
    change(items, { type: 'add', product_price_id: priceId });
  },
  removeFromCart(_productId, priceId) {
    change(
      get().items.filter((item) => item.selectedPrice.id !== priceId),
      { type: 'remove', product_price_id: priceId },
    );
  },
  adjustQuantity(_productId, priceId, delta) {
    const items = get()
      .items.map((item) =>
        item.selectedPrice.id === priceId
          ? { ...item, quantity: item.quantity + delta }
          : item,
      )
      .filter((item) => item.quantity > 0);
    change(items, { type: 'adjust', product_price_id: priceId, delta });
  },
  updateCartItemPrice(_productId, oldPriceId, newPriceId) {
    if (oldPriceId === newPriceId) return;
    const source = get().items.find(
      (item) => item.selectedPrice.id === oldPriceId,
    );
    const selectedPrice = source?.product.prices.find(
      (price) => price.id === newPriceId,
    );
    if (!source || !selectedPrice) return;
    const target = get().items.find(
      (item) => item.selectedPrice.id === newPriceId,
    );
    const items = get()
      .items.filter((item) => item.selectedPrice.id !== oldPriceId)
      .map((item) =>
        item.selectedPrice.id === newPriceId
          ? { ...item, quantity: item.quantity + source.quantity }
          : item,
      );
    if (!target) items.push({ ...source, selectedPrice });
    change(items, {
      type: 'change_price',
      product_price_id: oldPriceId,
      target_product_price_id: newPriceId,
    });
  },
  clearCart: () =>
    set({ items: [], totalPrice: 0, totalCount: 0, priceDetails: null }),
}));
